import { PaymentDirection, PrestationPricingMode, PrestationType } from '@cpce/database/enums';
import {
  faceValueCriteriaSchema,
  formatAccountLabel,
  hasDisbursementAccountIfCreance,
  hasOneSubsidyTargetUnlessAdhesion,
  isPrestationTypeConsistent,
  isSubsidisedByBilletterie,
  type PartialPrestationData,
  PRESTATION_CATEGORY_REQUIRED_ERROR,
  PRESTATION_KIND_RULES,
  PRESTATION_TYPE_RULES,
  type PrestationData,
  type TicketingCategory,
  toUTCMidnight,
} from '@cpce/shared';
import { getRuledCategoryIds, type SubsidyRule } from '@cpce/ticketing-api';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form';
import { z } from 'zod';
import { useAccountingCategories } from '@/api/accounting-categories/accounting-categories.queries';
import { useAccountingPeriods } from '@/api/accounting-periods/accounting-periods.queries';
import { usePaymentMethodAccounts } from '@/api/financial-accounts/financial-accounts.queries';
import {
  usePrestationAccount,
  useSubsidisingRule,
  useSubsidyProducts,
  useSubsidyRules,
} from '@/api/prestations/prestations.queries';
import type { PrestationDetail } from '@/api/prestations/prestations.types';
import { useSupplier } from '@/api/suppliers/suppliers.queries';
import { useCategories } from '@/api/transaction-categories/transaction-categories.queries';
import { AccountingCategoryMultiSelect } from '@/components/AccountingCategoryMultiSelect';
import { Button } from '@/components/Button';
import { CardEditActions } from '@/components/CardEditActions';
import { CardWithTitle } from '@/components/CardWithTitle';
import { Checkbox } from '@/components/Checkbox';
import { DatePicker } from '@/components/DatePicker';
import { FieldsGrid } from '@/components/FieldsGrid';
import { FormFooter } from '@/components/FormFooter';
import { Input } from '@/components/Input';
import { EntityLink } from '@/components/StyledLink';
import { SupplierCombobox } from '@/components/SupplierCombobox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shadcn/Select';
import { buildCategoryNameMap } from '@/utils/accounting-categories.utils';
import { PrestationAccountField } from './PrestationAccountField';
import { formatSubsidyProductLabel, SubsidyTargetCombobox } from './SubsidyTargetCombobox';

const buildPrestationFormSchema = (minParticipants: number) =>
  z
    .object({
      name: z.string().min(1, 'Le nom est requis'),
      maxParticipants: z
        .union([z.literal(''), z.nan(), z.number()])
        .transform((val, ctx) => {
          if (val === '' || (typeof val === 'number' && Number.isNaN(val))) {
            ctx.addIssue({ code: 'custom', message: 'Le nombre max doit être positif' });
            return z.NEVER;
          }
          return val as number;
        })
        .pipe(
          z
            .number()
            .int()
            .positive('Le nombre max doit être positif')
            .min(
              minParticipants,
              minParticipants > 0
                ? `Ne peut pas être inférieur aux ${minParticipants} participation(s) existante(s)`
                : 'Le nombre max doit être positif'
            )
        ),
      // Null until a choice is made, and on an adhésion left unsubsidised.
      ticketingCategoryId: z.number().int().positive().nullable(),
      ticketingProductVariantCode: z.string().nullable(),
      blocksBeyondSubsidyQuantity: z.boolean(),
      // 0 until a choice is made; null is « Aucun », the CSE provides the prestation itself.
      supplierId: z
        .number()
        .int()
        .nullable()
        .refine((value) => value !== 0, 'Le fournisseur est requis'),
      isAdhesion: z.boolean(),
      employeesOnly: z.boolean(),
      pricingMode: z.enum(PrestationPricingMode),
      type: z.enum(PrestationType),
      disbursementAccountId: z.number().int().positive().nullable().optional(),
      accountingPeriodId: z.number().int().positive("L'exercice comptable est requis"),
      validityStartDate: z.date(),
      registrationDeadline: z.date(),
      prices: z
        .array(
          z.object({
            name: z.string(),
            price: z.number(),
            attributionCriteria: faceValueCriteriaSchema.nullable().optional(),
          })
        )
        .optional(),
      accountingCategoryIds: z
        .array(z.number().int())
        .max(1, 'Une seule catégorie comptable est autorisée')
        .optional(),
    })
    .refine((data) => data.validityStartDate <= data.registrationDeadline, {
      message: "La date de validité ne peut pas être postérieure à la date limite d'inscription",
      path: ['registrationDeadline'],
    })
    .refine(hasOneSubsidyTargetUnlessAdhesion, {
      message: PRESTATION_CATEGORY_REQUIRED_ERROR,
      path: ['ticketingCategoryId'],
    })
    .refine(hasDisbursementAccountIfCreance, {
      message: 'Le compte de décaissement est requis',
      path: ['disbursementAccountId'],
    });

export type PrestationFormInput = z.input<ReturnType<typeof buildPrestationFormSchema>>;

// Exercice dates come from the API at UTC midnight; render them as the same calendar day in the
// local timezone so the DatePicker's day-level min/max matchers line up (no off-by-one).
function utcDateToLocalDay(date: Date): Date {
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

interface PrestationInformationBaseProps {
  isCreating?: boolean;
}

interface PrestationInformationCreateProps extends PrestationInformationBaseProps {
  isCreating: true;
  /** Prefills the form, e.g. when duplicating an existing prestation. */
  initialValues?: Partial<PrestationFormInput>;
  onSubmit: (data: PrestationData) => void;
  onCancel?: () => void;
}

interface PrestationInformationEditProps extends PrestationInformationBaseProps {
  isCreating?: false;
  prestation: PrestationDetail;
  onSave?: (data: PartialPrestationData) => void;
}

type PrestationInformationProps = PrestationInformationCreateProps | PrestationInformationEditProps;

export function PrestationInformation(props: PrestationInformationProps) {
  const isCreating = props.isCreating ?? false;
  const currentParticipants = props.isCreating
    ? 0
    : props.prestation._count.prestationParticipations;

  const getDefaultValues = (): PrestationFormInput => {
    if (props.isCreating) {
      return {
        name: '',
        maxParticipants: '',
        ticketingCategoryId: null,
        ticketingProductVariantCode: null,
        blocksBeyondSubsidyQuantity: false,
        supplierId: 0,
        isAdhesion: false,
        employeesOnly: false,
        pricingMode: PrestationPricingMode.predefinedPrices,
        type: PrestationType.billetterie,
        disbursementAccountId: null,
        accountingPeriodId: 0,
        validityStartDate: toUTCMidnight(new Date()),
        registrationDeadline: toUTCMidnight(new Date()),
        prices: [],
        accountingCategoryIds: [],
        ...props.initialValues,
      };
    }
    const p = props.prestation;
    return {
      name: p.name,
      maxParticipants: p.maxParticipants,
      ticketingCategoryId: p.ticketingCategoryId,
      ticketingProductVariantCode: p.ticketingProductVariantCode,
      blocksBeyondSubsidyQuantity: p.blocksBeyondSubsidyQuantity,
      supplierId: p.supplierId,
      isAdhesion: p.isAdhesion,
      employeesOnly: p.employeesOnly,
      pricingMode: p.pricingMode,
      type: p.type,
      disbursementAccountId: p.disbursementAccountId,
      accountingPeriodId: p.accountingPeriodId,
      validityStartDate: new Date(p.validityStartDate),
      registrationDeadline: new Date(p.registrationDeadline),
      prices: [],
      accountingCategoryIds: p.accountingCategories.map((c) => c.id),
    };
  };

  const form = useForm<PrestationFormInput, unknown, PrestationData>({
    resolver: zodResolver(buildPrestationFormSchema(currentParticipants)),
    defaultValues: getDefaultValues(),
  });

  const watchedSupplierId = form.watch('supplierId');
  const { data: supplier } = useSupplier(watchedSupplierId ?? 0, {
    enabled: isCreating && !!watchedSupplierId,
  });
  const supplierCategoryId = watchedSupplierId ? (supplier?.accountingCategoryId ?? null) : null;
  const prefilledCategoryIdRef = useRef<number | null>(null);

  // Follows the supplier as long as the category is empty or still the one prefilled from the
  // previous supplier: a category picked by hand, or copied by « Dupliquer », is never overwritten.
  useEffect(() => {
    if (!isCreating || (watchedSupplierId && !supplier)) return;
    const current = form.getValues('accountingCategoryIds') ?? [];
    const isPrefilled =
      current.length === 0 ||
      (current.length === 1 && current[0] === prefilledCategoryIdRef.current);
    if (!isPrefilled) return;
    form.setValue('accountingCategoryIds', supplierCategoryId !== null ? [supplierCategoryId] : []);
    prefilledCategoryIdRef.current = supplierCategoryId;
  }, [isCreating, watchedSupplierId, supplier, supplierCategoryId, form]);

  const handleFormSubmit = form.handleSubmit((data) => {
    if (props.isCreating) {
      props.onSubmit(data);
    }
  });

  const handleCardSave = async (cardFields: (keyof PrestationFormInput)[]) => {
    if (props.isCreating) return { success: true };
    const hasBothDates =
      cardFields.includes('validityStartDate') && cardFields.includes('registrationDeadline');
    const valid = hasBothDates ? await form.trigger() : await form.trigger(cardFields);
    if (!valid) return { success: false };
    const values = form.getValues();
    const cardData: PartialPrestationData = {};
    for (const field of cardFields) {
      (cardData as Record<string, unknown>)[field] = values[field];
    }
    props.onSave?.(cardData);
    return { success: true };
  };

  const handleCancel = () => {
    if (props.isCreating) {
      props.onCancel?.();
    }
  };

  const savedProductLabel = props.isCreating ? null : props.prestation.ticketingProductLabel;

  return (
    <FormProvider {...form}>
      <form onSubmit={handleFormSubmit} className="flex flex-col gap-6">
        <GeneralitesCard
          isCreating={isCreating}
          onCardSave={handleCardSave}
          currentParticipants={currentParticipants}
          savedProductLabel={savedProductLabel}
        />
        <ComptabiliteCard
          isCreating={isCreating}
          onCardSave={handleCardSave}
          prestationId={props.isCreating ? null : props.prestation.id}
        />
        <FournisseurCard
          isCreating={isCreating}
          onCardSave={handleCardSave}
          hasParticipations={currentParticipants > 0}
          wasAdhesion={!props.isCreating && props.prestation.isAdhesion}
          savedProductLabel={savedProductLabel}
        />
        {props.isCreating && (
          <FormFooter>
            {props.onCancel ? (
              <Button type="button" variant="outline" onClick={handleCancel}>
                Annuler
              </Button>
            ) : (
              <div />
            )}
            <Button type="submit" variant="color">
              Valider
            </Button>
          </FormFooter>
        )}
      </form>
    </FormProvider>
  );
}

interface CardProps {
  isCreating: boolean;
  onCardSave: (cardFields: (keyof PrestationFormInput)[]) => Promise<{ success: boolean }>;
}

interface GeneralitesCardProps extends CardProps {
  currentParticipants: number;
  savedProductLabel: string | null;
}

function GeneralitesCard({
  isCreating,
  onCardSave,
  currentParticipants,
  savedProductLabel,
}: GeneralitesCardProps) {
  const { data: categories = [] } = useCategories();
  const { data: subsidyProducts = [] } = useSubsidyProducts();
  const { data: accountingPeriods = [] } = useAccountingPeriods();
  const [isEditingCard, setIsEditingCard] = useState(false);

  const {
    register,
    watch,
    control,
    formState: { errors },
  } = useFormContext<PrestationFormInput>();

  const formValues = watch();
  const isEditing = isCreating || isEditingCard;

  const selectedPeriod = accountingPeriods.find((p) => p.id === formValues.accountingPeriodId);
  const periodMinDate = selectedPeriod
    ? utcDateToLocalDay(new Date(selectedPeriod.startDate))
    : undefined;
  const periodMaxDate = selectedPeriod
    ? utcDateToLocalDay(new Date(selectedPeriod.endDate))
    : undefined;

  const handleSave = async () => {
    const { success } = await onCardSave([
      'name',
      'maxParticipants',
      'employeesOnly',
      // A partial update is validated on what it carries: an empty target needs the flag.
      formValues.isAdhesion ? 'isAdhesion' : 'pricingMode',
      'ticketingCategoryId',
      'ticketingProductVariantCode',
      'blocksBeyondSubsidyQuantity',
      'validityStartDate',
      'registrationDeadline',
    ]);
    if (success) {
      setIsEditingCard(false);
    }
  };

  const pricingModes = PRESTATION_TYPE_RULES[formValues.type].pricingModes;
  const isSubsidised = isSubsidisedByBilletterie(formValues);
  const category = categories.find((c) => c.id === formValues.ticketingCategoryId);
  const selectedVariant = formValues.ticketingProductVariantCode;
  const productLabel = selectedVariant
    ? (subsidyProducts.find((p) => p.variantCode === selectedVariant)?.label ??
      savedProductLabel ??
      selectedVariant)
    : null;

  const formatDate = (date: Date | string | null | undefined) => {
    if (!date) return '-';
    const d = new Date(date);
    return d.toLocaleDateString('fr-FR');
  };

  return (
    <CardWithTitle
      title="Généralités"
      actions={
        !isCreating && (
          <CardEditActions
            isEditing={isEditing}
            onSave={handleSave}
            onEdit={() => setIsEditingCard(true)}
          />
        )
      }
    >
      <FieldsGrid
        fields={[
          {
            title: 'Type de prestation',
            hint: formValues.isAdhesion
              ? isSubsidised
                ? SUBSIDISED_ADHESION_HINT
                : ADHESION_HINT
              : undefined,
            value: isCreating ? (
              <PrestationTypeSelect />
            ) : (
              PRESTATION_TYPE_RULES[formValues.type].label
            ),
          },
          {
            title: 'Nom de la prestation',
            value: isEditing ? (
              <div className="flex flex-col gap-1">
                {errors.name && <span className="text-sm text-red-500">{errors.name.message}</span>}
                <Input
                  {...register('name')}
                  placeholder="Saisir le nom"
                  aria-invalid={!!errors.name}
                />
              </div>
            ) : (
              formValues.name
            ),
          },
          {
            title: "Nombre d'inscrits max",
            value: isEditing ? (
              <div className="flex flex-col gap-1">
                {errors.maxParticipants && (
                  <span className="text-sm text-red-500">{errors.maxParticipants.message}</span>
                )}
                <Input
                  type="number"
                  min={0}
                  {...register('maxParticipants', { valueAsNumber: true })}
                  placeholder="0"
                  aria-invalid={!!errors.maxParticipants}
                />
              </div>
            ) : formValues.maxParticipants != null ? (
              String(formValues.maxParticipants)
            ) : (
              '-'
            ),
          },
          {
            title: "Nombre d'inscrits actuel",
            value: String(currentParticipants),
          },
          {
            title: 'Réservée aux salariés',
            value: isEditing ? (
              <Controller
                name="employeesOnly"
                control={control}
                render={({ field }) => (
                  <div className="flex h-9 items-center gap-2 text-sm">
                    <Checkbox
                      id="prestation-employees-only"
                      checked={field.value}
                      onCheckedChange={(checked) => field.onChange(checked === true)}
                    />
                    <label htmlFor="prestation-employees-only">
                      Les ayants-droit ne peuvent pas être inscrits
                    </label>
                  </div>
                )}
              />
            ) : formValues.employeesOnly ? (
              'Oui'
            ) : (
              'Non'
            ),
          },
          ...(formValues.isAdhesion
            ? []
            : [
                {
                  title: 'Mode de tarification',
                  hint:
                    isEditing && currentParticipants > 0
                      ? 'La prestation a des inscrits : son mode de tarification ne peut plus changer.'
                      : undefined,
                  value:
                    isEditing && currentParticipants === 0 && pricingModes.length > 1 ? (
                      <Controller
                        name="pricingMode"
                        control={control}
                        render={({ field }) => (
                          <Select value={field.value} onValueChange={field.onChange}>
                            <SelectTrigger aria-label="Mode de tarification">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {pricingModes.map((value) => (
                                <SelectItem key={value} value={value}>
                                  {pricingModeLabel(formValues.type, value)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    ) : (
                      pricingModeLabel(formValues.type, formValues.pricingMode)
                    ),
                },
              ]),
          {
            title: 'Règle de subvention',
            hint:
              isEditing && currentParticipants > 0
                ? 'La prestation a des inscrits : elle ne peut plus changer de règle de subvention.'
                : undefined,
            value: isEditing ? (
              <SubsidyTargetField
                disabled={currentParticipants > 0}
                savedProductLabel={savedProductLabel}
              />
            ) : productLabel ? (
              formatSubsidyProductLabel(productLabel)
            ) : formValues.isAdhesion && !isSubsidised ? (
              ADHESION_CATEGORY_LABEL
            ) : (
              (category?.path ?? '-')
            ),
          },
          ...(isSubsidised
            ? [
                {
                  title: 'Quantité subventionnée',
                  hint:
                    isEditing || formValues.blocksBeyondSubsidyQuantity
                      ? SUBSIDY_QUANTITY_BLOCK_HINT
                      : undefined,
                  value: <SubsidyQuantityBlockField isEditing={isEditing} />,
                },
              ]
            : []),
          {
            title: 'Validité à partir du',
            value: isEditing ? (
              <Controller
                name="validityStartDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    className="w-full"
                    value={field.value ? new Date(field.value) : undefined}
                    onChange={(date) => field.onChange(date ?? null)}
                    minDate={periodMinDate}
                    maxDate={periodMaxDate}
                  />
                )}
              />
            ) : (
              formatDate(formValues.validityStartDate)
            ),
          },
          {
            title: "Date limite d'inscription",
            value: isEditing ? (
              <Controller
                name="registrationDeadline"
                control={control}
                render={({ field }) => (
                  <div className="flex flex-col gap-1">
                    <DatePicker
                      className="w-full"
                      value={field.value ? new Date(field.value) : undefined}
                      onChange={(date) => field.onChange(date ?? null)}
                      minDate={periodMinDate}
                      maxDate={periodMaxDate}
                    />
                    {errors.registrationDeadline && (
                      <span className="text-sm text-red-500">
                        {errors.registrationDeadline.message}
                      </span>
                    )}
                  </div>
                )}
              />
            ) : (
              formatDate(formValues.registrationDeadline)
            ),
          },
        ]}
      />
    </CardWithTitle>
  );
}

interface ComptabiliteCardProps extends CardProps {
  prestationId: number | null;
}

function ComptabiliteCard({ isCreating, onCardSave, prestationId }: ComptabiliteCardProps) {
  const { data: accountingPeriods = [] } = useAccountingPeriods();
  const { data: accountingCategories = [], isLoading: isLoadingAccountingCategories } =
    useAccountingCategories();
  const { data: accountState } = usePrestationAccount(prestationId ?? 0);
  const [isEditingCard, setIsEditingCard] = useState(false);

  const {
    watch,
    control,
    setValue,
    formState: { errors },
  } = useFormContext<PrestationFormInput>();

  const formValues = watch();

  useEffect(() => {
    if (isCreating && accountingPeriods.length > 0 && !formValues.accountingPeriodId) {
      setValue('accountingPeriodId', accountingPeriods[0].id);
    }
  }, [isCreating, accountingPeriods, setValue, formValues.accountingPeriodId]);
  const isEditing = isCreating || isEditingCard;

  const handleSave = () => {
    onCardSave(['accountingPeriodId', 'accountingCategoryIds']);
    setIsEditingCard(false);
  };

  const accountingPeriod = accountingPeriods.find((p) => p.id === formValues.accountingPeriodId);

  return (
    <CardWithTitle
      title="Comptabilité"
      actions={
        !isCreating && (
          <CardEditActions
            isEditing={isEditing}
            onSave={handleSave}
            onEdit={() => setIsEditingCard(true)}
          />
        )
      }
    >
      <FieldsGrid
        fields={[
          {
            title: 'Exercice comptable',
            value: isEditing ? (
              <div className="flex flex-col gap-1">
                {errors.accountingPeriodId && (
                  <span className="text-sm text-red-500">{errors.accountingPeriodId.message}</span>
                )}
                <Controller
                  name="accountingPeriodId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value ? String(field.value) : ''}
                      onValueChange={(val) => field.onChange(Number(val))}
                    >
                      <SelectTrigger aria-invalid={!!errors.accountingPeriodId}>
                        <SelectValue placeholder="Selectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        {accountingPeriods.map((period) => (
                          <SelectItem key={period.id} value={String(period.id)}>
                            {period.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            ) : (
              (accountingPeriod?.name ?? '-')
            ),
          },
          {
            title: 'Catégories comptables',
            value: isEditing ? (
              <Controller
                name="accountingCategoryIds"
                control={control}
                render={({ field }) => (
                  <AccountingCategoryMultiSelect
                    single
                    value={field.value ?? []}
                    onChange={field.onChange}
                    categories={accountingCategories}
                    isLoading={isLoadingAccountingCategories}
                  />
                )}
              />
            ) : (
              (() => {
                const nameMap = buildCategoryNameMap(accountingCategories);
                return (
                  formValues.accountingCategoryIds
                    ?.map((id) => nameMap.get(id))
                    .filter(Boolean)
                    .join(', ') || '-'
                );
              })()
            ),
          },
          ...(prestationId === null || !accountState
            ? []
            : [
                {
                  title: `Compte ${PRESTATION_KIND_RULES[accountState.kind].accountLabel}`,
                  value: <PrestationAccountField prestationId={prestationId} />,
                },
              ]),
        ]}
      />
    </CardWithTitle>
  );
}

const NO_SUPPLIER_LABEL = 'Aucun (prestation fournie par le CSE)';
const PRICING_MODE_LABELS: Record<PrestationPricingMode, string> = {
  predefinedPrices: 'Tarifs prédéfinis',
  typedAmount: "Montant saisi à l'inscription",
};

function pricingModeLabel(type: PrestationType, pricingMode: PrestationPricingMode): string {
  return type === PrestationType.cheques
    ? 'Valeurs faciales prédéfinies'
    : PRICING_MODE_LABELS[pricingMode];
}

const PRESTATION_TYPE_OPTIONS = Object.values(PrestationType).map((value) => ({
  value,
  label: PRESTATION_TYPE_RULES[value].label,
}));

// Everything the family decides follows it: supplier or none, adhésion, its default pricing mode.
function useApplyPrestationType() {
  const { getValues, setValue } = useFormContext<PrestationFormInput>();
  return (type: PrestationType) => {
    const rule = PRESTATION_TYPE_RULES[type];
    const wasAdhesion = getValues('type') === PrestationType.adhesion;
    setValue('type', type, { shouldDirty: true, shouldValidate: true });
    setValue('isAdhesion', type === PrestationType.adhesion);
    setValue('pricingMode', rule.pricingModes[0]);
    if (!rule.hasSupplier) {
      setValue('supplierId', null);
    } else if (getValues('supplierId') === null) {
      setValue('supplierId', 0);
    }
    if (type === PrestationType.adhesion) {
      setValue('ticketingCategoryId', null);
      setValue('ticketingProductVariantCode', null);
      setValue('employeesOnly', true);
    } else if (wasAdhesion) {
      setValue('employeesOnly', false);
    }
  };
}

// Once dossiers exist, only the families booking them the same way: same supplier or none, same
// adhésion, same pricing mode.
function PrestationTypeSelect({ keepsBooking = false }: { keepsBooking?: boolean }) {
  const { control, watch, setValue } = useFormContext<PrestationFormInput>();
  const applyType = useApplyPrestationType();
  const [supplierId, isAdhesion, pricingMode] = watch(['supplierId', 'isAdhesion', 'pricingMode']);
  const options = keepsBooking
    ? PRESTATION_TYPE_OPTIONS.filter((option) =>
        isPrestationTypeConsistent({ type: option.value, supplierId, isAdhesion, pricingMode })
      )
    : PRESTATION_TYPE_OPTIONS;
  return (
    <Controller
      name="type"
      control={control}
      render={({ field }) => (
        <Select
          value={field.value}
          onValueChange={(value) =>
            keepsBooking
              ? setValue('type', value as PrestationType, { shouldDirty: true })
              : applyType(value as PrestationType)
          }
          disabled={options.length < 2}
        >
          <SelectTrigger aria-label="Type de prestation">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    />
  );
}

const NO_CATEGORIES: TicketingCategory[] = [];
const NO_SUBSIDY_RULES: SubsidyRule[] = [];

function SubsidyTargetField({
  disabled,
  savedProductLabel,
}: {
  disabled: boolean;
  savedProductLabel: string | null;
}) {
  const { data: categories, isLoading: isLoadingCategories } = useCategories();
  const { data: subsidyProducts = [], isLoading: isLoadingSubsidyProducts } = useSubsidyProducts();
  const {
    data: subsidyRules,
    isLoading: isLoadingSubsidyRules,
    isError: hasSubsidyRulesError,
  } = useSubsidyRules();
  const ruledCategoryIds = useMemo(
    () =>
      hasSubsidyRulesError
        ? null
        : getRuledCategoryIds(categories ?? NO_CATEGORIES, subsidyRules ?? NO_SUBSIDY_RULES),
    [categories, subsidyRules, hasSubsidyRulesError]
  );
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<PrestationFormInput>();
  const [ticketingCategoryId, ticketingProductVariantCode, isAdhesion] = watch([
    'ticketingCategoryId',
    'ticketingProductVariantCode',
    'isAdhesion',
  ]);

  return (
    <div className="flex flex-col gap-1">
      {errors.ticketingCategoryId && (
        <span className="text-sm text-red-500">{errors.ticketingCategoryId.message}</span>
      )}
      <SubsidyTargetCombobox
        disabled={disabled}
        categories={categories ?? NO_CATEGORIES}
        ruledCategoryIds={ruledCategoryIds}
        products={subsidyProducts}
        value={{ ticketingCategoryId, ticketingProductVariantCode }}
        savedProductLabel={savedProductLabel}
        noTargetLabel={isAdhesion ? ADHESION_CATEGORY_LABEL : undefined}
        onChange={(target) => {
          setValue('ticketingProductVariantCode', target.ticketingProductVariantCode, {
            shouldDirty: true,
          });
          setValue('ticketingCategoryId', target.ticketingCategoryId, {
            shouldDirty: true,
            shouldValidate: true,
          });
        }}
        error={errors.ticketingCategoryId?.message}
        isLoading={isLoadingCategories || isLoadingSubsidyProducts || isLoadingSubsidyRules}
      />
    </div>
  );
}

const ADHESION_CATEGORY_LABEL = 'Aucune (adhésion, sans subvention)';
const ADHESION_HINT =
  "Cotisation due par le salarié au CSE, comptabilisée en 756 « Cotisations » : aucune subvention, et l'inscription crée une dette que le salarié règle.";
const SUBSIDISED_ADHESION_HINT =
  "Cotisation due par le salarié au CSE, comptabilisée en 756 « Cotisations » : la billetterie en subventionne une part, et l'inscription crée une dette du reste, que le salarié règle.";

const SUBSIDY_QUANTITY_BLOCK_HINT =
  "Refuse l'inscription quand un salarié et ses ayants-droit dépasseraient le nombre de places que la billetterie peut encore subventionner, achats faits directement sur la billetterie compris, en tenant compte des inscriptions non réglées de l'exercice sur la même règle. Sans cette option, les places au-delà sont vendues sans subvention.";

function SubsidyQuantityBlockField({ isEditing }: { isEditing: boolean }) {
  const { watch, control } = useFormContext<PrestationFormInput>();
  const categoryId = watch('ticketingCategoryId');
  const variantCode = watch('ticketingProductVariantCode');
  const isBlocking = watch('blocksBeyondSubsidyQuantity');

  const ruleQuery = useMemo(() => {
    if (variantCode) return { ticketingProductVariantCode: variantCode };
    return categoryId ? { ticketingCategoryId: categoryId } : null;
  }, [variantCode, categoryId]);
  const { data, isLoading, isError } = useSubsidisingRule(
    isEditing || isBlocking ? ruleQuery : null
  );

  const describeRule = (): string | null => {
    if (!ruleQuery) return null;
    if (isLoading) return 'recherche de la règle…';
    if (isError) return 'règle illisible, billetterie injoignable';
    if (!data?.rule) return 'aucune règle de subvention en vigueur';
    return data.rule.quantity === null
      ? `règle « ${data.rule.target} » : sans limite`
      : `règle « ${data.rule.target} » : ${data.rule.quantity} max`;
  };
  const ruleDescription = describeRule();

  if (!isEditing) {
    return isBlocking
      ? `Bloquée au-delà${ruleDescription ? ` (${ruleDescription})` : ''}`
      : 'Non bloquée';
  }
  return (
    <Controller
      name="blocksBeyondSubsidyQuantity"
      control={control}
      render={({ field }) => (
        <div className="flex h-9 items-center gap-2 text-sm">
          <Checkbox
            id="prestation-blocks-beyond-subsidy-quantity"
            checked={field.value}
            onCheckedChange={(checked) => field.onChange(checked === true)}
          />
          <label htmlFor="prestation-blocks-beyond-subsidy-quantity">
            Bloquer au-delà de la quantité subventionnée
          </label>
          {ruleDescription && <span className="text-gray-500">({ruleDescription})</span>}
        </div>
      )}
    />
  );
}

interface FournisseurCardProps extends CardProps {
  hasParticipations: boolean;
  wasAdhesion: boolean;
  savedProductLabel: string | null;
}

function FournisseurCard({
  isCreating,
  onCardSave,
  hasParticipations,
  wasAdhesion,
  savedProductLabel,
}: FournisseurCardProps) {
  const { data: disbursementAccounts = [] } = usePaymentMethodAccounts(PaymentDirection.outflow);
  const [isEditingCard, setIsEditingCard] = useState(false);

  const {
    watch,
    control,
    formState: { errors },
  } = useFormContext<PrestationFormInput>();

  const formValues = watch();
  const isEditing = isCreating || isEditingCard;
  const isSupplierLess = formValues.supplierId === null;
  const leavesAdhesion = wasAdhesion && !formValues.isAdhesion;

  const handleSave = async () => {
    const { success } = await onCardSave([
      'type',
      'supplierId',
      'isAdhesion',
      'pricingMode',
      'employeesOnly',
      'disbursementAccountId',
      ...(wasAdhesion || formValues.isAdhesion
        ? (['ticketingCategoryId', 'ticketingProductVariantCode'] as const)
        : []),
    ]);
    if (success) {
      setIsEditingCard(false);
    }
  };

  const { data: supplier } = useSupplier(formValues.supplierId ?? 0, {
    enabled: !!formValues.supplierId,
  });
  const disbursementAccount = disbursementAccounts.find(
    (account) => account.id === formValues.disbursementAccountId
  );

  const renderSupplierLink = () => {
    if (isSupplierLess) return NO_SUPPLIER_LABEL;
    if (!supplier) return '-';
    return (
      <EntityLink entity="supplier" id={supplier.id}>
        {supplier.name}
      </EntityLink>
    );
  };

  return (
    <CardWithTitle
      title="Fournisseur"
      actions={
        !isCreating && (
          <CardEditActions
            isEditing={isEditing}
            onSave={handleSave}
            onEdit={() => setIsEditingCard(true)}
          />
        )
      }
    >
      <FieldsGrid
        fields={[
          ...(isCreating || !isEditing
            ? []
            : [
                {
                  title: 'Type de prestation',
                  hint: hasParticipations
                    ? 'La prestation a des inscrits : seuls les types qui la comptabilisent de la même façon sont proposés.'
                    : undefined,
                  value: <PrestationTypeSelect keepsBooking={hasParticipations} />,
                },
              ]),
          ...(isEditing && leavesAdhesion
            ? [
                {
                  title: 'Règle de subvention',
                  value: (
                    <SubsidyTargetField disabled={false} savedProductLabel={savedProductLabel} />
                  ),
                },
              ]
            : []),
          {
            title: 'Fournisseur',
            hint:
              isEditing && hasParticipations && !isSupplierLess
                ? 'La prestation a des inscrits : elle ne peut plus changer de fournisseur.'
                : undefined,
            value:
              isEditing && !isSupplierLess ? (
                <div className="flex flex-col gap-1">
                  {errors.supplierId && (
                    <span className="text-sm text-red-500">{errors.supplierId.message}</span>
                  )}
                  <Controller
                    name="supplierId"
                    control={control}
                    render={({ field }) => (
                      <SupplierCombobox
                        value={field.value}
                        onChange={field.onChange}
                        disabled={hasParticipations}
                        error={errors.supplierId?.message}
                      />
                    )}
                  />
                </div>
              ) : (
                renderSupplierLink()
              ),
          },
          ...(isSupplierLess && !formValues.isAdhesion
            ? [
                {
                  title: 'Compte de décaissement',
                  hint: isEditing
                    ? 'Compte bancaire depuis lequel les montants dus aux bénéficiaires leur sont versés.'
                    : undefined,
                  value: isEditing ? (
                    <div className="flex flex-col gap-1">
                      {errors.disbursementAccountId && (
                        <span className="text-sm text-red-500">
                          {errors.disbursementAccountId.message}
                        </span>
                      )}
                      <Controller
                        name="disbursementAccountId"
                        control={control}
                        render={({ field }) => (
                          <Select
                            value={field.value ? String(field.value) : ''}
                            onValueChange={(val) => field.onChange(Number(val))}
                          >
                            <SelectTrigger aria-invalid={!!errors.disbursementAccountId}>
                              <SelectValue placeholder="Selectionner un compte de banque..." />
                            </SelectTrigger>
                            <SelectContent>
                              {disbursementAccounts.map((account) => (
                                <SelectItem key={account.id} value={String(account.id)}>
                                  {formatAccountLabel(account)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                  ) : disbursementAccount ? (
                    formatAccountLabel(disbursementAccount)
                  ) : (
                    '-'
                  ),
                },
              ]
            : []),
        ]}
      />
    </CardWithTitle>
  );
}
